<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import LandingPageButton from '@shared/components/LandingPageButton.vue'
import LandingPageToolbar from '@shared/components/LandingPageToolbar.vue'
import { LandingButtonAppSettings } from '@shared/models/interfaces/branding'
import { Nullable } from '@shared/models/types/primary'
import { useUserLocale } from '@shared/utils/composables/locales'
import colors from '@shared/utils/constants/colors'
import getLocalizedLabel from '@shared/utils/helpers/localized-label'

import { ButtonShapeType } from '@protos/button_shape_type_pb'
import { ChatWidgetMessageType } from '@protos/chat_widget_message_type_pb'

import { useCreatePatientConversation } from '@pc/composables/create-conversation'
import routeNames from '@pc/router/route-names'
import { useDemoModeStore } from '@pc/store/demo'
import { useSessionStore } from '@pc/store/session'

const FaqBuilder = defineAsyncComponent(
  () => import('@shared/components/FaqBuilder.vue')
)

const route = useRoute()
const sessionStore = useSessionStore()
const { userLocale } = useUserLocale()
const demoModeStore = useDemoModeStore()
const { t } = useI18n()
const { loading, createPatientConversation } = useCreatePatientConversation()

const routeQuery = computed(() => route.query)
const widget = computed(() => sessionStore.chatProps?.chatWidget)
const widgetName = computed(() => {
  const name = widget.value?.name

  return name?.length
    ? getLocalizedLabel(name, userLocale.value).value
    : (widget.value?.description ?? t('unknown'))
})
const faqs = computed(() => widget.value?.faqs ?? [])
const splashImage = computed(() => widget.value?.branding?.splashScreenUrl)

const landingMessage = computed(() =>
  widget.value?.messages?.find(
    (m) => m.messageType === ChatWidgetMessageType.START
  )
)

const customizationEntity = computed(() =>
  landingMessage.value?.customization?.case === 'startButton'
    ? landingMessage.value?.customization.value
    : null
)

const buttonSettings = computed<Nullable<LandingButtonAppSettings>>(() => {
  if (!customizationEntity.value) {
    return {
      text: t('chat.labels.startConversation'),
      classNames: ['tw-text-white'],
      animated: false,
      bg: colors.blue600,
      hasIcon: false,
      icon: { value: '', isAppended: false },
      iconAppend: false,
      rounded: false,
    }
  }

  try {
    const {
      isAnimated: animated,
      color: bg,
      shaping,
      icon,
    } = customizationEntity.value

    document.documentElement.style.setProperty(
      '--landing-btn-color',
      `${bg || ''}`
    )

    const classNames = shaping.map(
      (shapingEl: ButtonShapeType) =>
        `landing-btn--${ButtonShapeType[+shapingEl].toLowerCase()}`
    )

    const settings: LandingButtonAppSettings = {
      text:
        getLocalizedLabel(
          landingMessage.value?.localizedTexts ?? [],
          userLocale.value
        ).value || t('chat.labels.startConversation'),
      classNames,
      animated,
      bg: ['text', 'outlined'].some((type) =>
        classNames.join('').includes(type)
      )
        ? 'transparent'
        : bg,
      hasIcon: !!icon?.value,
      icon: icon!,
      rounded: classNames.join('').includes('round'),
    }

    return settings
  } catch (error) {
    return null
  }
})

const handleToolbarClose = () => {
  demoModeStore.updateDemoChatVisibility(false)
}
</script>

<template>
  <section v-loading.fullscreen="loading" class="tw-flex-1 tw-flex tw-flex-col">
    <header
      v-if="splashImage"
      class="header--splash tw-flex tw-bg-cover tw-bg-no-repeat tw-bg-center tw-flex-col tw-items-center tw-justify-center"
      :class="[faqs.length ? 'tw-h-3/5' : 'tw-h-full']"
      :style="{
        backgroundImage: `url(${splashImage})`,
      }"
    >
      <LandingPageToolbar
        :route="{ name: routeNames.help, query: routeQuery }"
        class="tw-absolute header--splash__toolbar"
      />

      <LandingPageButton
        :show-old-conversations="
          !widget?.hideOldConversationForPatientVisitor &&
          !Boolean(widget?.isOneConversationPerPatientOrVisitor)
        "
        :button-settings="buttonSettings"
        :route="{ name: routeNames.conversationsList }"
        @create-conversation="createPatientConversation"
      />
    </header>

    <header v-else class="header--default tw-relative">
      <div class="tw-flex tw-items-center tw-justify-between tw-p-4">
        <span class="tw-text-white tw-text-lg">
          {{ widgetName }}
        </span>

        <LandingPageToolbar
          :route="{ name: routeNames.help, query: routeQuery }"
          @close="handleToolbarClose"
        />
      </div>

      <div class="header__bottom" />

      <el-card
        shadow="never"
        :body-style="{ padding: '10px' }"
        class="tw-flex tw-justify-center tw--m-5 tw-mx-6 tw-relative tw-z-10"
      >
        <LandingPageButton
          :show-old-conversations="
            !widget?.hideOldConversationForPatientVisitor &&
            !Boolean(widget?.isOneConversationPerPatientOrVisitor) &&
            !widget?.isKioskModeEnabled
          "
          :route="{ name: routeNames.conversationsList }"
          :button-settings="buttonSettings"
          @create-conversation="createPatientConversation"
        />
      </el-card>
    </header>

    <div
      v-if="faqs.length"
      class="tw-pt-8 tw-p-5 tw-flex-grow tw-overflow-auto tw-h-0"
    >
      <FaqBuilder :faqs="faqs" />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.header {
  &--splash {
    &__toolbar {
      right: 15px;
      top: 15px;
    }
  }

  &--default {
    background-color: var(--branding-color, var(--el-color-primary));
  }

  &__bottom {
    background-color: var(--branding-color, var(--el-color-primary));
    height: 52px;
  }
}
</style>
